import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft,
  Home,
  RotateCcw,
  Play,
  Pause,
  Square,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Sparkles,
  PartyPopper,
  Download,
} from "lucide-react";

import { useAuth } from "../AuthContext";
import { t } from "../i18n";

/* ---------------------- Audio Player ---------------------- */
function AudioPlayer({ src, lang }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setCurrent(a.currentTime);
    const onMeta = () => {
      const d = isFinite(a.duration) ? a.duration : 0;
      setDuration(d);
      setReady(true);
    };
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, []);

  const play = async () => {
    try { await audioRef.current?.play(); setPlaying(true); }
    catch (_e) { toast.error("Couldn't start audio"); }
  };
  const pause = () => { audioRef.current?.pause(); setPlaying(false); };
  const stop = () => {
    const a = audioRef.current; if (!a) return;
    a.pause(); a.currentTime = 0; setCurrent(0); setPlaying(false);
  };
  const seek = (e) => {
    const a = audioRef.current; if (!a) return;
    const v = Number(e.target.value); a.currentTime = v; setCurrent(v);
  };

  const fmt = (s) => {
    if (!isFinite(s) || s < 0) s = 0;
    const m = Math.floor(s / 60); const r = Math.floor(s % 60);
    return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
  };

  return (
    <div
      data-testid="lang-audio-player"
      className="mt-4 bg-white rounded-3xl p-4 border-4 border-white shadow-[0_6px_0_rgba(0,0,0,0.08)]"
    >
      <audio ref={audioRef} src={src} preload="metadata" />
      <div className="flex items-center gap-3">
        {playing ? (
          <button data-testid="audio-pause-btn" onClick={pause}
            className="w-12 h-12 rounded-full bg-amber-400 text-white flex items-center justify-center shadow-[0_3px_0_rgba(0,0,0,0.15)] active:translate-y-0.5">
            <Pause size={22} strokeWidth={3} />
          </button>
        ) : (
          <button data-testid="audio-play-btn" onClick={play} disabled={!ready}
            className="w-12 h-12 rounded-full bg-emerald-500 disabled:bg-slate-200 disabled:text-slate-400 text-white flex items-center justify-center shadow-[0_3px_0_rgba(0,0,0,0.15)] active:translate-y-0.5">
            <Play size={22} strokeWidth={3} />
          </button>
        )}
        <button data-testid="audio-stop-btn" onClick={stop}
          className="w-12 h-12 rounded-full bg-rose-400 text-white flex items-center justify-center shadow-[0_3px_0_rgba(0,0,0,0.15)] active:translate-y-0.5">
          <Square size={20} strokeWidth={3} />
        </button>
        <div className="flex-1 min-w-0">
          <input data-testid="audio-seek" type="range" min={0} max={duration || 0} step={0.1} value={current} onChange={seek}
            disabled={!ready || !duration} className="w-full accent-violet-500" />
          <div className="flex items-center justify-between text-[11px] font-bold text-slate-500 mt-1 tabular-nums">
            <span data-testid="audio-current">{fmt(current)}</span>
            <span data-testid="audio-duration">{fmt(duration)}</span>
          </div>
        </div>
      </div>
      <p className="text-[11px] text-slate-400 font-semibold mt-2">{t(lang, "audio_summary_label")}</p>
    </div>
  );
}

/* ---------------------- MCQ Quiz ---------------------- */
// Compose a short WebAudio chime locally — no asset hosting, no autoplay block on user-interaction.
function playChime(kind) {
  try {
    const ACtx = window.AudioContext || window.webkitAudioContext;
    if (!ACtx) return;
    const ac = new ACtx();
    const now = ac.currentTime;
    const notes = kind === "correct"
      ? [{ f: 660, t: 0 }, { f: 880, t: 0.13 }, { f: 1320, t: 0.26 }]
      : [{ f: 220, t: 0 }, { f: 165, t: 0.18 }];
    notes.forEach(({ f, t }) => {
      const o = ac.createOscillator();
      const g = ac.createGain();
      o.type = kind === "correct" ? "triangle" : "sawtooth";
      o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, now + t);
      g.gain.exponentialRampToValueAtTime(0.22, now + t + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.25);
      o.connect(g).connect(ac.destination);
      o.start(now + t);
      o.stop(now + t + 0.3);
    });
    setTimeout(() => ac.close().catch(() => {}), 1100);
  } catch (_) { /* silent */ }
}

function arraysEqualAsSets(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  const A = [...a].sort();
  const B = [...b].sort();
  return A.every((v, i) => v === B[i]);
}

function MCQQuiz({ title, questions, lang, onExit, onAnother }) {
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);

  const total = questions.length;
  const q = questions[idx];
  const multi = (q?.correct_answer || []).length > 1;

  const toggle = (i) => {
    if (submitted) return;
    setPicked((prev) => {
      if (multi) {
        return prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i];
      }
      return [i];
    });
  };

  const submit = () => {
    if (submitted || picked.length === 0) return;
    const isCorrect = arraysEqualAsSets(picked, q.correct_answer || []);
    setSubmitted(true);
    if (isCorrect) {
      setScore((s) => s + 1);
      playChime("correct");
      toast.success(t(lang, "mcq_correct"));
    } else {
      playChime("wrong");
      toast.error(t(lang, "mcq_wrong"));
    }
  };

  const next = () => {
    if (idx + 1 >= total) { setDone(true); return; }
    setIdx(idx + 1); setPicked([]); setSubmitted(false);
  };

  if (done) {
    const allCorrect = score === total;
    return (
      <motion.div
        data-testid="mcq-done"
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-3xl p-7 border-4 border-white shadow-[0_8px_0_rgba(0,0,0,0.08)] text-center"
      >
        <motion.div
          animate={{ rotate: [0, -10, 10, -8, 8, 0], y: [0, -10, 0, -8, 0] }}
          transition={{ duration: 1.6, repeat: Infinity }}
          className={"mx-auto w-24 h-24 rounded-full flex items-center justify-center shadow-[0_8px_0_rgba(0,0,0,0.12)] " +
            (allCorrect ? "bg-emerald-400" : "bg-amber-400")}
        >
          <PartyPopper size={48} strokeWidth={2.5} className="text-white" />
        </motion.div>
        <h2 className="font-heading text-3xl font-bold mt-5 text-white drop-shadow-[0_3px_10px_rgba(15,23,42,0.55)]">
          {allCorrect ? t(lang, "mcq_all_correct") : t(lang, "mcq_done_title")}
        </h2>
        <p className="mt-2 text-base text-cyan-50/95 font-semibold drop-shadow-[0_2px_8px_rgba(15,23,42,0.45)]">
          {t(lang, "your_score")}: <b className="text-white">{score} / {total}</b>
        </p>
        <div className="mt-6 grid grid-cols-2 gap-3">
          <button
            data-testid="mcq-another-btn"
            onClick={onAnother}
            className="bg-white border-2 border-slate-900/10 text-slate-700 font-heading font-bold uppercase tracking-tight text-xs py-4 px-2 rounded-full btn-squish inline-flex items-center justify-center gap-1.5 whitespace-nowrap leading-none"
          >
            <RotateCcw size={16} strokeWidth={2.75} className="shrink-0" />
            <span>{t(lang, "try_another")}</span>
          </button>
          <button
            data-testid="mcq-home-btn"
            onClick={onExit}
            className="bg-purple-400 hover:bg-purple-500 text-white font-heading font-bold uppercase tracking-tight text-xs py-4 px-2 rounded-full btn-squish inline-flex items-center justify-center gap-1.5 whitespace-nowrap leading-none"
          >
            <Home size={16} strokeWidth={2.75} className="shrink-0" />
            <span>{t(lang, "home")}</span>
          </button>
        </div>
      </motion.div>
    );
  }

  // Active question
  const correctSet = new Set(q.correct_answer || []);
  const pickedSet = new Set(picked);
  const wholeAnswerCorrect = submitted && arraysEqualAsSets(picked, q.correct_answer || []);

  return (
    <div data-testid="mcq-quiz">
      {/* Progress */}
      <div className="flex items-center justify-between text-sm font-bold text-slate-500 mb-2 mt-1">
        <span data-testid="mcq-progress">{t(lang, "question")} {idx + 1} / {total}</span>
        <span className="inline-flex items-center gap-1 text-violet-600">
          <Sparkles size={14} strokeWidth={2.75} /> {q.dimension || ""}
        </span>
      </div>
      <div className="h-2 w-full bg-white rounded-full overflow-hidden border-2 border-white shadow-inner">
        <motion.div
          className="h-full bg-gradient-to-r from-amber-400 to-violet-500"
          initial={false}
          animate={{ width: `${((idx + (submitted ? 1 : 0.5)) / total) * 100}%` }}
          transition={{ type: "spring", stiffness: 90, damping: 16 }}
        />
      </div>

      {title && <h2 className="mt-4 font-heading text-xl font-bold text-slate-700">{title}</h2>}

      <motion.div
        key={q.id}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-3 bg-white rounded-3xl p-5 border-4 border-white shadow-[0_8px_0_rgba(0,0,0,0.08)]"
      >
        <p data-testid="mcq-question" className="font-heading text-lg font-bold text-slate-800 leading-snug">
          {q.question}
        </p>
        {multi && (
          <p className="text-[11px] font-bold text-violet-600 mt-1">
            {t(lang, "multi_select_hint")}
          </p>
        )}

        <div className="mt-4 space-y-2.5">
          {q.options.map((opt, i) => {
            const isPicked = pickedSet.has(i);
            const isCorrect = correctSet.has(i);
            let cls = "bg-slate-50 text-slate-800 border-slate-200";
            if (!submitted) {
              cls = isPicked
                ? "bg-violet-500 text-white border-violet-700 shadow-[0_3px_0_rgba(0,0,0,0.18)]"
                : "bg-slate-50 text-slate-800 border-slate-200";
            } else {
              if (isCorrect) cls = "bg-emerald-100 text-emerald-900 border-emerald-400 ring-2 ring-emerald-400";
              else if (isPicked) cls = "bg-rose-100 text-rose-900 border-rose-400 ring-2 ring-rose-400";
              else cls = "bg-slate-50 text-slate-600 border-slate-200 opacity-70";
            }
            return (
              <button
                key={i}
                disabled={submitted}
                data-testid={`mcq-option-${i}`}
                onClick={() => toggle(i)}
                className={`w-full text-left px-4 py-3 rounded-2xl border-2 font-semibold text-base transition-all relative ${cls} disabled:cursor-default flex items-start gap-3 leading-snug`}
              >
                <span className={"shrink-0 w-7 h-7 rounded-lg font-heading font-extrabold text-sm flex items-center justify-center " +
                  (submitted && isCorrect ? "bg-emerald-500 text-white" :
                   submitted && isPicked  ? "bg-rose-500 text-white"    :
                   !submitted && isPicked ? "bg-white text-violet-600"  :
                                             "bg-white text-slate-500 border border-slate-200")}>
                  {String.fromCharCode(65 + i)}
                </span>
                <span className="flex-1 min-w-0">{opt}</span>
                {submitted && isCorrect && <CheckCircle2 size={20} strokeWidth={2.75} className="text-emerald-600 shrink-0" />}
                {submitted && !isCorrect && isPicked && <XCircle size={20} strokeWidth={2.75} className="text-rose-600 shrink-0" />}
              </button>
            );
          })}
        </div>

        {/* Feedback banner */}
        <AnimatePresence>
          {submitted && (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className={"mt-4 rounded-2xl p-3 flex items-start gap-3 font-bold " +
                (wholeAnswerCorrect ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800")}
            >
              {wholeAnswerCorrect ? <CheckCircle2 size={20} strokeWidth={2.75} /> : <XCircle size={20} strokeWidth={2.75} />}
              <div className="text-sm">
                {wholeAnswerCorrect ? t(lang, "mcq_correct_feedback") : t(lang, "mcq_wrong_feedback")}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* CTA */}
      {!submitted ? (
        <button
          data-testid="mcq-submit-btn"
          onClick={submit}
          disabled={picked.length === 0}
          className="mt-5 w-full bg-emerald-400 hover:bg-emerald-500 disabled:bg-slate-200 disabled:text-slate-400 text-white font-heading text-lg font-bold uppercase tracking-wide py-4 rounded-full btn-squish"
        >
          {t(lang, "mcq_submit")}
        </button>
      ) : (
        <button
          data-testid="mcq-next-btn"
          onClick={next}
          className="mt-5 w-full bg-violet-500 hover:bg-violet-600 text-white font-heading text-lg font-bold uppercase tracking-wide py-4 rounded-full btn-squish"
        >
          {idx + 1 >= total ? t(lang, "mcq_see_score") : t(lang, "mcq_next")}
        </button>
      )}
    </div>
  );
}

/* ---------------------- Page ---------------------- */
export default function LanguageResult() {
  const navigate = useNavigate();
  const { lang } = useAuth();
  const [item, setItem] = useState(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("dt_lang_result");
      if (!raw) { navigate("/language", { replace: true }); return; }
      setItem(JSON.parse(raw));
    } catch (_) {
      navigate("/language", { replace: true });
    }
  }, [navigate]);

  const audioSrc = useMemo(() => {
    if (!item?.audio_b64 || !item?.audio_mime) return null;
    return `data:${item.audio_mime};base64,${item.audio_b64}`;
  }, [item]);

  if (!item) return null;

  const isSummary = item.type === "lang-summary";
  const isMCQ = item.type === "lang-mcq";
  const isCheck = item.type === "lang-check-answer";
  const isNature = item.type === "lang-learn-nature";
  const captureType = isSummary
    ? "summary"
    : isCheck
    ? "check-answer"
    : isNature
    ? "learn-nature"
    : "mcq";

  const noContent =
    (isSummary && (item.text_output || "").trim().toUpperCase().startsWith("NO CONTENT FOUND")) ||
    (isMCQ && (!item.questions || item.questions.length === 0)) ||
    (isCheck && /question paper not found/i.test(item?.html || "")) ||
    (isNature && (item.text_output || "").trim().toUpperCase().startsWith("NO CONTENT FOUND"));

  const titleKey = isSummary
    ? "lang_summary"
    : isMCQ
    ? "lang_mcq"
    : isCheck
    ? "lang_check_answer"
    : "lang_learn_nature";

  const onDownloadCheck = () => {
    if (!item?.html) return;
    try {
      const blob = new Blob([item.html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `saral-eye-answer-check-${Date.now()}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Saved!");
    } catch (_) {
      toast.error("Download failed");
    }
  };

  return (
    <div className="page-shell" data-testid="lang-result-page">
      <div className="flex items-center justify-between mb-4">
        <button
          data-testid="back-btn"
          onClick={() => navigate("/language")}
          className="p-3 rounded-full bg-white border-2 border-slate-900/10 btn-squish"
        >
          <ArrowLeft size={22} strokeWidth={2.5} className="text-slate-700" />
        </button>
        <h2 className="font-heading text-2xl font-bold text-white drop-shadow-[0_3px_10px_rgba(15,23,42,0.55)]">
          {t(lang, titleKey)}
        </h2>
        <div className="w-12" />
      </div>

      {noContent && (
        <div data-testid="lang-no-content-banner"
          className="bg-amber-50 border-4 border-amber-200 rounded-3xl p-4 flex items-start gap-3 text-amber-800">
          <AlertTriangle size={22} strokeWidth={2.5} className="text-amber-600 mt-0.5 shrink-0" />
          <div>
            <div className="font-bold">{t(lang, isCheck ? "no_paper_found_title" : "no_content_title")}</div>
            <div className="text-sm font-semibold mt-0.5">{t(lang, isCheck ? "no_paper_found_desc" : "no_content_desc")}</div>
          </div>
        </div>
      )}

      {!noContent && isCheck && (
        <>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl p-2 border-4 border-white shadow-[0_8px_0_rgba(0,0,0,0.08)] overflow-hidden"
          >
            <div className="w-full h-[70vh] rounded-2xl overflow-auto bg-[#fdfcf7]">
              <iframe
                data-testid="lang-check-frame"
                title="answer-check"
                sandbox=""
                srcDoc={item.html}
                scrolling="yes"
                className="block bg-[#fdfcf7] border-0"
                style={{ width: "1100px", height: "1500px" }}
              />
            </div>
            <p className="mt-2 text-center text-[11px] text-slate-500 font-semibold">
              Pinch to zoom · scroll in both directions to read full notebook
            </p>
          </motion.div>

          <button
            data-testid="lang-check-download-btn"
            onClick={onDownloadCheck}
            className="mt-6 w-full bg-emerald-400 hover:bg-emerald-500 text-white font-heading text-xl font-bold uppercase tracking-wide py-4 rounded-full btn-squish flex items-center justify-center gap-2"
          >
            <Download size={22} strokeWidth={2.5} /> {t(lang, "download")}
          </button>

          <div className="mt-3 grid grid-cols-2 gap-3">
            <button
              data-testid="lang-check-another-btn"
              onClick={() => navigate(`/language/capture/${captureType}`)}
              className="bg-white border-2 border-slate-900/10 text-slate-700 font-heading font-bold uppercase tracking-tight text-xs py-4 px-2 rounded-full btn-squish inline-flex items-center justify-center gap-1.5 whitespace-nowrap leading-none"
            >
              <RotateCcw size={16} strokeWidth={2.75} className="shrink-0" />
              <span>{t(lang, "check_another")}</span>
            </button>
            <button
              data-testid="lang-check-home-btn"
              onClick={() => navigate("/")}
              className="bg-purple-400 hover:bg-purple-500 text-white font-heading font-bold uppercase tracking-tight text-xs py-4 px-2 rounded-full btn-squish inline-flex items-center justify-center gap-1.5 whitespace-nowrap leading-none"
            >
              <Home size={16} strokeWidth={2.75} className="shrink-0" />
              <span>{t(lang, "home")}</span>
            </button>
          </div>
        </>
      )}

      {!noContent && (isSummary || isNature) && (
        <>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl p-5 border-4 border-white shadow-[0_8px_0_rgba(0,0,0,0.08)]"
          >
            <h3 className="font-heading text-lg font-bold text-slate-700 mb-3">
              {t(lang, isNature ? "nature_lessons_label" : "summary_label")}
            </h3>
            <ul data-testid={isNature ? "lang-nature-text" : "lang-summary-text"} className="space-y-2.5">
              {(item.text_output || "")
                .split(/\r?\n/)
                .map((line) => line.trim())
                .map((line) => line.replace(/^[•\-*]\s*/, ""))
                .filter(Boolean)
                .map((line, i) => (
                  <li key={i} className="flex items-start gap-3 text-base text-slate-800 font-medium leading-relaxed">
                    <span aria-hidden className={"mt-2 w-2.5 h-2.5 shrink-0 rounded-full ring-2 shadow-[0_2px_0_rgba(0,0,0,0.1)] " +
                      (isNature ? "bg-gradient-to-br from-lime-400 to-emerald-500 ring-lime-200" : "bg-gradient-to-br from-amber-400 to-violet-500 ring-amber-200")} />
                    <span className="min-w-0">{line}</span>
                  </li>
                ))}
            </ul>
          </motion.div>

          {audioSrc ? (
            <AudioPlayer src={audioSrc} lang={lang} />
          ) : (
            <div className="mt-4 text-center text-xs text-slate-500 font-semibold" data-testid="audio-unavailable-msg">
              {item.audio_error ? `Audio: ${item.audio_error}` : t(lang, "audio_unavailable")}
            </div>
          )}

          <div className="mt-3 grid grid-cols-2 gap-3">
            <button
              data-testid="lang-another-btn"
              onClick={() => navigate(`/language/capture/${captureType}`)}
              className="bg-white border-2 border-slate-900/10 text-slate-700 font-heading font-bold uppercase tracking-tight text-xs py-4 px-2 rounded-full btn-squish inline-flex items-center justify-center gap-1.5 whitespace-nowrap leading-none"
            >
              <RotateCcw size={16} strokeWidth={2.75} className="shrink-0" />
              <span>{t(lang, "learn_another")}</span>
            </button>
            <button
              data-testid="lang-home-btn"
              onClick={() => navigate("/")}
              className="bg-purple-400 hover:bg-purple-500 text-white font-heading font-bold uppercase tracking-tight text-xs py-4 px-2 rounded-full btn-squish inline-flex items-center justify-center gap-1.5 whitespace-nowrap leading-none"
            >
              <Home size={16} strokeWidth={2.75} className="shrink-0" />
              <span>{t(lang, "home")}</span>
            </button>
          </div>
        </>
      )}

      {!noContent && isMCQ && (
        <MCQQuiz
          title={item.title}
          questions={item.questions}
          lang={lang}
          onExit={() => navigate("/")}
          onAnother={() => navigate(`/language/capture/${captureType}`)}
        />
      )}

      {noContent && (
        <div className="mt-3 grid grid-cols-2 gap-3">
          <button
            data-testid="lang-check-another-btn"
            onClick={() => navigate(`/language/capture/${captureType}`)}
            className="bg-white border-2 border-slate-900/10 text-slate-700 font-heading font-bold uppercase tracking-tight text-xs py-4 px-2 rounded-full btn-squish inline-flex items-center justify-center gap-1.5 whitespace-nowrap leading-none"
          >
            <RotateCcw size={16} strokeWidth={2.75} className="shrink-0" />
            <span>{t(lang, isSummary || isNature ? "learn_another" : isCheck ? "check_another" : "try_another")}</span>
          </button>
          <button
            data-testid="lang-check-home-btn"
            onClick={() => navigate("/")}
            className="bg-purple-400 hover:bg-purple-500 text-white font-heading font-bold uppercase tracking-tight text-xs py-4 px-2 rounded-full btn-squish inline-flex items-center justify-center gap-1.5 whitespace-nowrap leading-none"
          >
            <Home size={16} strokeWidth={2.75} className="shrink-0" />
            <span>{t(lang, "home")}</span>
          </button>
        </div>
      )}
    </div>
  );
}
